// The UI controller. Imperative render with section refreshes - small enough not to
// need a framework, deliberate enough not to look like one. All crypto runs in the
// engine; this file only renders ciphertext-free plaintext from the encrypted store.
import { h, mount } from "./dom.js";
import { icon } from "./icons.js";
import { initDeterrents } from "./deterrents.js";
import { register, login, unlock, logout, savedAccount, exportRecovery, importRecovery } from "../app/session.js";
import type { Engine, PurgeWindow } from "../app/engine.js";
import type { ConvSocket } from "../net/socket.js";
import type { Settings, StoredConversation, StoredMessage } from "../store/idb.js";
import { DEFAULT_SETTINGS } from "../store/idb.js";
import type { ServerEvent } from "@shared/wire.js";
import { TTL_PRESETS } from "@shared/constants.js";
import { ApiError } from "../net/api.js";

const HANDLE_HINT = "3-20 letters, numbers, dots or underscores";
const handleOk = (s: string) => /^[a-zA-Z0-9_.]{3,20}$/.test(s);
const GROUP_WINDOW = 5 * 60 * 1000; // group consecutive messages from one sender within 5 min
const peerOf = (c: StoredConversation, me: string) => c.members.find((m) => m.user_id !== me);

export class App {
  private engine: Engine | null = null;
  private socket: ConvSocket | null = null;
  private activeConv: string | null = null;
  private currentConv: StoredConversation | null = null;
  private settings: Settings = { ...DEFAULT_SETTINGS };
  private sweepTimer = 0;

  // Ephemeral, per-open-conversation state.
  private typingUsers = new Map<string, number>();
  private online = new Set<string>();
  private peerRead = new Map<string, number>();
  private typingEl: HTMLElement | null = null;
  private subEl: HTMLElement | null = null;
  private lastTypingSent = 0;
  private lastReadSent = 0;
  private lastShot = 0;

  constructor(private root: HTMLElement) {}

  async start(): Promise<void> {
    initDeterrents();
    this.installGlobalKeys();
    const acct = await savedAccount();
    if (acct && acct.salt) this.screenUnlock(acct.handle);
    else this.screenAuth("create");
  }

  // Shift-to-reveal quick delete, and best-effort screenshot-key detection.
  private installGlobalKeys(): void {
    const setShift = (on: boolean) => document.body.classList.toggle("shift-mode", on);
    window.addEventListener("keydown", (e) => {
      if ((e as KeyboardEvent).key === "Shift") setShift(true);
      this.onScreenshotKey(e as KeyboardEvent);
    });
    window.addEventListener("keyup", (e) => {
      if ((e as KeyboardEvent).key === "Shift") setShift(false);
      this.onScreenshotKey(e as KeyboardEvent);
    });
    window.addEventListener("blur", () => setShift(false));
  }

  private onScreenshotKey(e: KeyboardEvent): void {
    const shot = e.key === "PrintScreen" || (e.metaKey && e.shiftKey && ["3", "4", "5"].includes(e.key));
    if (!shot) return;
    const now = Date.now();
    if (now - this.lastShot < 1500) return; // de-dupe keydown+keyup
    this.lastShot = now;
    if (this.socket && this.activeConv) this.socket.send({ type: "screenshot" });
  }

  // ------------------------------------------------------------- toasts + popups

  private toast(msg: string, kind: "info" | "error" = "info"): void {
    const t = h("div", { class: `toast ${kind}` }, msg);
    document.body.append(t);
    requestAnimationFrame(() => t.classList.add("show"));
    setTimeout(() => {
      t.classList.remove("show");
      setTimeout(() => t.remove(), 250);
    }, 3200);
  }

  private modal(title: string, body: HTMLElement, actions?: HTMLElement[]): () => void {
    const close = () => overlay.remove();
    const card = h(
      "div",
      { class: "modal", role: "dialog", "aria-modal": "true", "aria-label": title },
      h("header", { class: "modal-head" }, h("h2", null, title), button(icon("x"), close, "icon-btn", "Close")),
      h("div", { class: "modal-body" }, body),
      actions && actions.length ? h("footer", { class: "modal-foot" }, ...actions) : null,
    );
    const overlay = h("div", { class: "overlay", onclick: (e: Event) => e.target === overlay && close() }, card);
    document.body.append(overlay);
    return close;
  }

  /** A right-click style popup menu anchored at a point. */
  private popup(x: number, y: number, items: (HTMLElement | null)[]): void {
    const menu = h("div", { class: "ctx-menu" }, ...items.filter(Boolean));
    const overlay = h("div", {
      class: "ctx-overlay",
      onclick: () => overlay.remove(),
      oncontextmenu: (e: Event) => {
        e.preventDefault();
        overlay.remove();
      },
    });
    overlay.append(menu);
    document.body.append(overlay);
    const r = menu.getBoundingClientRect();
    menu.style.left = `${Math.min(x, window.innerWidth - r.width - 8)}px`;
    menu.style.top = `${Math.min(y, window.innerHeight - r.height - 8)}px`;
  }

  // ------------------------------------------------------------- auth screens

  private screenAuth(mode: "create" | "signin"): void {
    const handle = input({ placeholder: "yourname", autocomplete: "username" });
    const pass = input({ placeholder: "your password", type: "password", autocomplete: "current-password" });
    const confirm = input({ placeholder: "repeat password", type: "password", autocomplete: "new-password" });
    const submit = button(mode === "create" ? "Create my account" : "Sign in", () => go(), "btn primary wide");

    const go = async () => {
      const u = handle.value.trim();
      if (!handleOk(u)) return this.toast(`Pick a name. ${HANDLE_HINT}.`, "error");
      if (pass.value.length < 8) return this.toast("Use at least 8 characters for your password.", "error");
      if (mode === "create" && pass.value !== confirm.value) return this.toast("Those passwords don't match.", "error");
      submit.setAttribute("disabled", "");
      submit.textContent = "One moment...";
      try {
        const engine = mode === "create" ? await register(u, pass.value, (c) => this.onChange(c)) : await login(u, pass.value, (c) => this.onChange(c));
        await this.enter(engine, mode === "create");
      } catch (err) {
        submit.removeAttribute("disabled");
        submit.textContent = mode === "create" ? "Create my account" : "Sign in";
        this.toast(errText(err), "error");
      }
    };
    for (const el of [handle, pass, confirm]) el.addEventListener("keydown", (e) => (e as KeyboardEvent).key === "Enter" && go());

    const form = h(
      "div",
      { class: "auth-form" },
      field("Your name", handle, mode === "create" ? HANDLE_HINT : "the name you signed up with"),
      field("Password", pass, mode === "create" ? "Never leaves this device. We literally can't reset it." : null),
      mode === "create" ? field("Confirm password", confirm) : null,
      submit,
      h(
        "p",
        { class: "auth-switch" },
        mode === "create" ? "Already have an account? " : "New here? ",
        link(mode === "create" ? "Sign in" : "Create an account", () => this.screenAuth(mode === "create" ? "signin" : "create")),
      ),
      mode === "signin" ? link("Restore from a recovery file", () => this.restoreFlow()) : null,
    );

    mount(
      this.root,
      h("div", { class: "auth" }, brandPanel(), h("div", { class: "auth-pane" }, h("div", { class: "auth-card" }, h("h1", null, mode === "create" ? "Create your account" : "Welcome back"), form))),
    );
    handle.focus();
  }

  private screenUnlock(handle: string): void {
    const pass = input({ placeholder: "your password", type: "password", autocomplete: "current-password" });
    const submit = button("Unlock", () => go(), "btn primary wide");
    const go = async () => {
      submit.setAttribute("disabled", "");
      submit.textContent = "Unlocking...";
      try {
        await this.enter(await unlock(pass.value, (c) => this.onChange(c)), false);
      } catch {
        submit.removeAttribute("disabled");
        submit.textContent = "Unlock";
        this.toast("That password didn't work.", "error");
      }
    };
    pass.addEventListener("keydown", (e) => (e as KeyboardEvent).key === "Enter" && go());
    mount(
      this.root,
      h(
        "div",
        { class: "auth" },
        brandPanel(),
        h(
          "div",
          { class: "auth-pane" },
          h(
            "div",
            { class: "auth-card" },
            h("h1", null, "Welcome back, ", h("span", { class: "accent" }, handle)),
            field("Password", pass, "to decrypt your messages on this device"),
            submit,
            h("p", { class: "auth-switch" }, link("Sign in as someone else", async () => {
              await logout();
              this.screenAuth("signin");
            })),
          ),
        ),
      ),
    );
    pass.focus();
  }

  private async restoreFlow(): Promise<void> {
    const file = h("input", { type: "file", accept: "application/json,.json" }) as HTMLInputElement;
    const close = this.modal(
      "Restore from a recovery file",
      h("div", { class: "stack" }, h("p", { class: "muted" }, "Choose the recovery file you saved earlier. You'll still need your password. It's the only thing that can decrypt your keys."), file),
      [
        button("Restore", async () => {
          const f = file.files?.[0];
          if (!f) return this.toast("Pick a file first.", "error");
          try {
            await importRecovery(await f.text());
            close();
            this.toast("Recovery file loaded. Now sign in with your password.");
            this.screenAuth("signin");
          } catch (err) {
            this.toast(errText(err), "error");
          }
        }, "btn primary"),
      ],
    );
  }

  // ------------------------------------------------------------- enter app

  private async enter(engine: Engine, isNew: boolean): Promise<void> {
    this.engine = engine;
    this.settings = await engine.getSettings();
    await engine.syncConversations().catch(() => {});
    this.renderApp();
    this.startSweep();
    if (isNew) this.welcomeDialog();
  }

  private startSweep(): void {
    clearInterval(this.sweepTimer);
    this.sweepTimer = setInterval(() => {
      if (this.engine && this.activeConv) this.engine.sweepExpired(this.activeConv);
    }, 15000) as unknown as number;
  }

  private welcomeDialog(): void {
    const close = this.modal(
      "You're set up, privately",
      h(
        "div",
        { class: "stack" },
        h("p", null, "Your messages are end-to-end encrypted. We store only scrambled text we can't read, and your password never leaves this device."),
        h("p", { class: "warn" }, h("strong", null, "If you forget your password, your messages can't be recovered."), " That's the trade for real privacy."),
        h("p", { class: "muted" }, "Save a recovery file now so you can move to another device. You'll still need your password to use it."),
      ),
      [button("Save recovery file", () => this.downloadRecovery(), "btn"), button("Got it", () => close(), "btn primary")],
    );
  }

  private async downloadRecovery(): Promise<void> {
    try {
      const blob = new Blob([await exportRecovery()], { type: "application/json" });
      const a = h("a", { href: URL.createObjectURL(blob), download: `hush-recovery-${this.engine!.me.handle}.json` });
      a.click();
      URL.revokeObjectURL(a.href);
      this.toast("Recovery file saved. Keep it somewhere safe.");
    } catch (err) {
      this.toast(errText(err), "error");
    }
  }

  // ------------------------------------------------------------- app layout

  private listEl = h("div", { class: "conv-list" });
  private mainEl = h("main", { class: "main" });
  private appEl: HTMLElement | null = null;

  private renderApp(): void {
    const me = this.engine!.me;
    const sidebar = h(
      "aside",
      { class: "sidebar" },
      h(
        "header",
        { class: "side-head" },
        h("div", { class: "wordmark" }, h("span", { class: "lock" }, icon("lock", 18)), "Hush"),
        h("div", { class: "side-actions" }, button(icon("plus"), () => this.newChatDialog(), "icon-btn", "New chat"), button(icon("settings"), () => this.settingsDialog(), "icon-btn", "Settings")),
      ),
      this.listEl,
      h("footer", { class: "side-foot" }, h("span", { class: "avatar dm small" }, me.handle.slice(0, 1).toUpperCase()), h("span", { class: "you" }, me.handle)),
    );
    this.appEl = h("div", { class: "app" }, sidebar, this.mainEl);
    mount(this.root, this.appEl);
    this.refreshList();
    this.renderEmptyMain();
  }

  private async refreshList(): Promise<void> {
    if (!this.engine) return;
    const convs = await this.engine.conversations();
    const rows = await Promise.all(convs.map(async (c) => ({ c, last: (await this.engine!.messages(c.conv_id)).at(-1) })));
    rows.sort((a, b) => (b.last?.ts ?? 0) - (a.last?.ts ?? 0));
    mount(
      this.listEl,
      rows.length === 0 ? h("div", { class: "list-empty" }, h("p", null, "No conversations yet."), link("Start one", () => this.newChatDialog())) : rows.map(({ c, last }) => this.convRow(c, last)),
    );
  }

  private convRow(c: StoredConversation, last?: StoredMessage): HTMLElement {
    const title = convTitle(c, this.engine!.me.user_id);
    const preview = last ? (last.kind === "system" ? last.text ?? "" : last.kind === "file" ? "Attachment" : last.text ?? "") : "No messages yet";
    return h(
      "button",
      {
        class: `conv-row ${this.activeConv === c.conv_id ? "active" : ""}`,
        onclick: () => this.openConv(c.conv_id),
        oncontextmenu: (e: Event) => this.convRowMenu(e as MouseEvent, c),
      },
      h("div", { class: `avatar ${c.type}` }, c.type === "group" ? icon("users", 18) : title.slice(0, 1).toUpperCase()),
      h(
        "div",
        { class: "conv-meta" },
        h("div", { class: "conv-row-top" }, h("span", { class: "conv-name" }, title), last ? h("time", null, shortTime(last.ts)) : null),
        h("div", { class: "conv-prev no-capture" }, preview),
      ),
    );
  }

  // ------------------------------------------------------------- chat

  private async openConv(convId: string): Promise<void> {
    this.activeConv = convId;
    this.appEl?.classList.add("chat-open");
    this.socket?.close();
    this.typingUsers.clear();
    this.online.clear();
    this.peerRead.clear();
    this.lastReadSent = 0;
    await this.refreshList();
    await this.renderChat();
    this.socket = this.engine!.connectSocket(convId, (ev) => this.onEvent(convId, ev), this.settings.presence);
  }

  private backToList(): void {
    this.activeConv = null;
    this.currentConv = null;
    this.appEl?.classList.remove("chat-open");
    this.socket?.close();
    this.socket = null;
    this.renderEmptyMain();
    this.refreshList();
  }

  private renderEmptyMain(): void {
    mount(
      this.mainEl,
      h(
        "div",
        { class: "empty-main" },
        h("div", { class: "empty-badge" }, icon("lock", 28)),
        h("h2", null, "Your conversations are end-to-end encrypted"),
        h("p", { class: "muted" }, "Pick a chat on the left, or start a new one. Only you and the people you message can read what's said."),
      ),
    );
  }

  private async renderChat(): Promise<void> {
    const conv = await this.engine!.conversation(this.activeConv!);
    if (!conv) return this.renderEmptyMain();
    this.currentConv = conv;
    const title = convTitle(conv, this.engine!.me.user_id);

    this.subEl = h("div", { class: "sub" }, conv.disappearing_ttl > 0 ? `Disappearing - ${ttlLabel(conv.disappearing_ttl)}` : "End-to-end encrypted");
    const header = h(
      "header",
      { class: "chat-head" },
      button(icon("back"), () => this.backToList(), "icon-btn only-mobile", "Back"),
      h("div", { class: `avatar ${conv.type}` }, conv.type === "group" ? icon("users", 18) : title.slice(0, 1).toUpperCase()),
      h("div", { class: "chat-title" }, h("div", { class: "name" }, title), this.subEl),
      h(
        "div",
        { class: "chat-actions" },
        conv.type === "dm" ? button(icon("shield"), () => this.verifyDialog(conv), "icon-btn", "Verify") : null,
        button(icon("clock"), () => this.disappearingDialog(conv), "icon-btn", "Disappearing messages"),
        conv.type === "group" ? button(icon("users"), () => this.membersDialog(conv), "icon-btn", "Members") : null,
        button(icon("more"), (e: Event) => this.convMenu(e as MouseEvent, conv), "icon-btn", "More"),
      ),
    );

    const messages = h("div", { class: "messages" });
    this.typingEl = h("div", { class: "typing", style: { visibility: "hidden" } });
    mount(this.mainEl, h("div", { class: "chat" }, header, h("div", { class: "messages-wrap" }, messages), this.typingEl, this.composer(conv)));
    this.updatePresenceSub();
    await this.refreshChat(messages);
  }

  private async refreshChat(target?: HTMLElement): Promise<void> {
    if (!this.activeConv || !this.engine) return;
    const list = target ?? (this.mainEl.querySelector(".messages") as HTMLElement | null);
    if (!list) return;
    const conv = this.currentConv ?? (await this.engine.conversation(this.activeConv));
    if (!conv) return;
    const me = this.engine.me.user_id;
    const now = Date.now();
    const msgs = (await this.engine.messages(this.activeConv)).filter((m) => !(m.expires_at != null && m.expires_at <= now));
    const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 80;

    const rows: HTMLElement[] = [];
    let prev: StoredMessage | null = null;
    for (const m of msgs) {
      if (m.kind === "system") {
        rows.push(h("div", { class: "sys-msg" }, icon("camera", 13), h("span", null, m.text ?? "")));
        prev = null;
        continue;
      }
      const grouped = !!prev && prev.sender_id === m.sender_id && m.ts - prev.ts < GROUP_WINDOW;
      rows.push(this.messageRow(conv, m, me, !grouped));
      prev = m;
    }
    // DM read receipt under the last own message.
    if (conv.type === "dm") {
      const peer = peerOf(conv, me);
      const readSeq = peer ? this.peerRead.get(peer.user_id) ?? 0 : 0;
      const lastOwn = [...msgs].reverse().find((m) => m.outgoing && m.kind !== "system");
      if (lastOwn && readSeq >= lastOwn.seq) rows.push(h("div", { class: "read-marker" }, "Seen"));
    }

    mount(list, rows.length ? rows : h("div", { class: "chat-hint" }, "Say hello. Your first message starts a private session."));
    if (nearBottom) list.scrollTop = list.scrollHeight;

    // Send a read receipt if we opted in and there's something new from the peer.
    if (this.settings.readReceipts && conv.last_seq > this.lastReadSent && document.hasFocus()) {
      this.lastReadSent = conv.last_seq;
      this.socket?.send({ type: "read", seq: conv.last_seq });
    }
  }

  private messageRow(conv: StoredConversation, m: StoredMessage, me: string, showHeader: boolean): HTMLElement {
    const mine = m.sender_id === me;
    const name = mine ? this.engine!.me.handle : conv.members.find((x) => x.user_id === m.sender_id)?.handle ?? "unknown";
    const body = m.kind === "file" && m.file ? this.fileChip(conv, m.file) : h("div", { class: "msg-text no-capture" }, m.text ?? "");

    const quickDel = button(icon("trash", 16), (e: Event) => {
      e.stopPropagation();
      this.quickDelete(m);
    }, "msg-act quick-del", mine ? "Delete for everyone" : "Delete on this device");
    const more = button(icon("more", 16), (e: Event) => this.msgMenu(e as MouseEvent, conv, m), "msg-act", "More");

    return h(
      "div",
      { class: `msg-row ${showHeader ? "head" : "cont"}`, oncontextmenu: (e: Event) => this.msgMenu(e as MouseEvent, conv, m) },
      h("div", { class: "msg-gutter" }, showHeader ? h("div", { class: "avatar dm sm" }, name.slice(0, 1).toUpperCase()) : h("time", { class: "hover-time" }, clockTime(m.ts))),
      h(
        "div",
        { class: "msg-main" },
        showHeader ? h("div", { class: "msg-head" }, h("span", { class: "author" }, name), h("time", null, fullTime(m.ts)), m.expires_at ? icon("clock", 12) : null) : null,
        body,
      ),
      h("div", { class: "msg-actions" }, quickDel, more),
    );
  }

  private async quickDelete(m: StoredMessage): Promise<void> {
    try {
      if (m.outgoing) await this.engine!.deleteForEveryone(m.conv_id, m.msg_id);
      else await this.engine!.deleteLocal(m.conv_id, m.msg_id);
    } catch (err) {
      this.toast(errText(err), "error");
    }
  }

  private fileChip(conv: StoredConversation, file: NonNullable<StoredMessage["file"]>): HTMLElement {
    return h(
      "button",
      {
        class: "file-chip",
        onclick: async () => {
          try {
            const bytes = await this.engine!.attachmentBytes(conv.conv_id, file);
            const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: file.mime || "application/octet-stream" }));
            const a = h("a", { href: url, download: file.name });
            a.click();
            URL.revokeObjectURL(url);
          } catch {
            this.toast("Couldn't open that attachment.", "error");
          }
        },
      },
      icon("download", 18),
      h("span", { class: "fname" }, file.name),
      h("span", { class: "fsize" }, humanSize(file.size)),
    );
  }

  private composer(conv: StoredConversation): HTMLElement {
    const ta = h("textarea", { class: "composer-input", placeholder: `Message ${convTitle(conv, this.engine!.me.user_id)}`, rows: "1", "aria-label": "Message" }) as HTMLTextAreaElement;
    const fileInput = h("input", { type: "file", style: { display: "none" } }) as HTMLInputElement;
    const send = async () => {
      const text = ta.value.trim();
      if (!text) return;
      ta.value = "";
      ta.style.height = "auto";
      try {
        await this.engine!.sendText(conv.conv_id, text);
      } catch (err) {
        this.toast(errText(err), "error");
      }
    };
    ta.addEventListener("input", () => {
      ta.style.height = "auto";
      ta.style.height = `${Math.min(ta.scrollHeight, 180)}px`;
      this.maybeTyping();
    });
    ta.addEventListener("keydown", (e) => {
      const k = e as KeyboardEvent;
      if (k.key === "Enter" && !k.shiftKey) {
        k.preventDefault();
        send();
      }
    });
    fileInput.addEventListener("change", async () => {
      const f = fileInput.files?.[0];
      if (!f) return;
      try {
        await this.engine!.sendFile(conv.conv_id, { name: f.name, mime: f.type, bytes: new Uint8Array(await f.arrayBuffer()) });
      } catch (err) {
        this.toast(errText(err), "error");
      }
      fileInput.value = "";
    });
    return h("div", { class: "composer" }, button(icon("paperclip"), () => fileInput.click(), "icon-btn", "Attach a file"), ta, fileInput, button(icon("send"), send, "icon-btn send", "Send"));
  }

  private maybeTyping(): void {
    if (!this.settings.typing) return;
    const now = Date.now();
    if (now - this.lastTypingSent < 2500) return;
    this.lastTypingSent = now;
    this.socket?.send({ type: "typing" });
  }

  // ------------------------------------------------------------- realtime events

  private onEvent(convId: string, ev: ServerEvent): void {
    switch (ev.type) {
      case "typing":
        return this.onTyping(ev.user_id);
      case "presence":
        return this.onPresence(ev.user_id, ev.online);
      case "read":
        return this.onRead(ev.user_id, ev.seq);
      case "screenshot":
        return void this.onScreenshot(convId, ev.user_id);
      default:
        void this.engine!.handleEvent(convId, ev);
    }
  }

  private nameFor(userId: string): string {
    if (userId === this.engine?.me.user_id) return "You";
    return this.currentConv?.members.find((m) => m.user_id === userId)?.handle ?? "Someone";
  }

  private onTyping(userId: string): void {
    if (userId === this.engine?.me.user_id) return;
    clearTimeout(this.typingUsers.get(userId));
    this.typingUsers.set(userId, setTimeout(() => {
      this.typingUsers.delete(userId);
      this.renderTyping();
    }, 4000) as unknown as number);
    this.renderTyping();
  }

  private renderTyping(): void {
    if (!this.typingEl) return;
    const names = [...this.typingUsers.keys()].map((id) => this.nameFor(id));
    this.typingEl.textContent = names.length === 0 ? "" : names.length === 1 ? `${names[0]} is typing...` : "Several people are typing...";
    this.typingEl.style.visibility = names.length ? "visible" : "hidden";
  }

  private onPresence(userId: string, isOnline: boolean): void {
    if (isOnline) this.online.add(userId);
    else this.online.delete(userId);
    this.updatePresenceSub();
  }

  private updatePresenceSub(): void {
    if (!this.subEl || !this.currentConv) return;
    const conv = this.currentConv;
    if (conv.disappearing_ttl > 0) return; // keep the disappearing label
    if (conv.type === "dm") {
      const peer = peerOf(conv, this.engine!.me.user_id);
      this.subEl.textContent = peer && this.online.has(peer.user_id) ? "Online" : "End-to-end encrypted";
    }
  }

  private onRead(userId: string, seq: number): void {
    this.peerRead.set(userId, Math.max(seq, this.peerRead.get(userId) ?? 0));
    this.refreshChat();
  }

  private async onScreenshot(convId: string, userId: string): Promise<void> {
    if (convId !== this.activeConv) return;
    await this.engine!.addSystemMessage(convId, `${this.nameFor(userId)} may have taken a screenshot`);
  }

  // ------------------------------------------------------------- menus & dialogs

  private msgMenu(e: MouseEvent, conv: StoredConversation, m: StoredMessage): void {
    e.preventDefault();
    this.popup(e.clientX, e.clientY, [
      m.kind === "text" && m.text ? menuItem("Copy text", icon("copy"), () => void navigator.clipboard?.writeText(m.text!).catch(() => {})) : null,
      conv.type === "dm" ? menuItem("Verify safety number", icon("shield"), () => this.verifyDialog(conv)) : null,
      menuItem("Copy message ID", icon("copy"), () => void navigator.clipboard?.writeText(m.msg_id).catch(() => {})),
      m.outgoing ? menuItem("Delete for everyone", icon("trash"), () => void this.engine!.deleteForEveryone(m.conv_id, m.msg_id).catch((err) => this.toast(errText(err), "error")), "danger") : null,
      menuItem("Delete on this device", icon("trash"), () => void this.engine!.deleteLocal(m.conv_id, m.msg_id)),
    ]);
  }

  private convRowMenu(e: Event, conv: StoredConversation): void {
    const ev = e as MouseEvent;
    ev.preventDefault();
    this.popup(ev.clientX, ev.clientY, [
      menuItem("Open", icon("reply"), () => this.openConv(conv.conv_id)),
      menuItem("Disappearing messages", icon("clock"), () => this.disappearingDialog(conv)),
      conv.type === "group" ? menuItem("Members", icon("users"), () => this.membersDialog(conv)) : null,
      conv.type === "dm" ? menuItem("Verify safety number", icon("shield"), () => this.verifyDialog(conv)) : null,
      menuItem("Clear history", icon("trash"), () => this.purgeDialog(conv), "danger"),
    ]);
  }

  private convMenu(e: MouseEvent, conv: StoredConversation): void {
    this.popup(e.clientX, e.clientY, [
      menuItem("Disappearing messages", icon("clock"), () => this.disappearingDialog(conv)),
      conv.type === "dm" ? menuItem("Verify safety number", icon("shield"), () => this.verifyDialog(conv)) : null,
      conv.type === "group" ? menuItem("Members", icon("users"), () => this.membersDialog(conv)) : null,
      menuItem("Clear history", icon("trash"), () => this.purgeDialog(conv), "danger"),
    ]);
  }

  private purgeDialog(conv: StoredConversation): void {
    let win: PurgeWindow = "hour";
    const everyone = h("input", { type: "checkbox" }) as HTMLInputElement;
    const pick = (w: PurgeWindow, label: string) =>
      h("label", { class: "radio" }, h("input", { type: "radio", name: "purge", checked: w === "hour", onchange: () => (win = w) }), label);
    const close = this.modal(
      "Clear history",
      h(
        "div",
        { class: "stack" },
        h("p", { class: "muted" }, "Remove messages from this conversation."),
        pick("hour", "Last hour"),
        pick("day", "Last 24 hours"),
        pick("all", "Everything"),
        h("label", { class: "check" }, everyone, h("span", null, "Also delete ", h("strong", null, "my own"), " messages for everyone (others' stay on their devices)")),
        h("p", { class: "tiny muted" }, "Clearing always affects this device. The for-everyone option only removes messages you sent."),
      ),
      [
        button("Cancel", () => close(), "btn"),
        button("Clear", async () => {
          try {
            await this.engine!.purge(conv.conv_id, win, everyone.checked);
            this.toast("History cleared on this device.");
          } catch (err) {
            this.toast(errText(err), "error");
          }
          close();
          this.refreshChat();
        }, "btn danger"),
      ],
    );
  }

  private disappearingDialog(conv: StoredConversation): void {
    let chosen = conv.disappearing_ttl;
    const opts = TTL_PRESETS.map((t) => h("label", { class: "radio" }, h("input", { type: "radio", name: "ttl", checked: t === conv.disappearing_ttl, onchange: () => (chosen = t) }), t === 0 ? "Off" : ttlLabel(t)));
    const close = this.modal(
      "Disappearing messages",
      h("div", { class: "stack" }, h("p", { class: "muted" }, "New messages auto-delete on every device after the timer. This is the most honest kind of deletion: there's nothing left to recover."), ...opts),
      [
        button("Cancel", () => close(), "btn"),
        button("Save", async () => {
          try {
            await this.engine!.setDisappearing(conv.conv_id, chosen);
            this.toast(chosen === 0 ? "Disappearing messages off." : `Messages disappear after ${ttlLabel(chosen)}.`);
          } catch (err) {
            this.toast(errText(err), "error");
          }
          close();
          if (conv.conv_id === this.activeConv) this.renderChat();
        }, "btn primary"),
      ],
    );
  }

  private verifyDialog(conv: StoredConversation): void {
    const peer = peerOf(conv, this.engine!.me.user_id);
    if (!peer) return;
    this.modal(
      "Verify your safety number",
      h(
        "div",
        { class: "stack" },
        h("p", { class: "muted" }, "If you and ", h("strong", null, peer.handle), " see the same number, no one is intercepting your messages. Compare it in person or over a call you trust."),
        h("div", { class: "safety" }, this.engine!.safetyNumberWith(peer.ik_sig_pub)),
      ),
    );
  }

  private async membersDialog(conv: StoredConversation): Promise<void> {
    const me = this.engine!.me.user_id;
    const amAdmin = conv.members.find((m) => m.user_id === me)?.role === "admin";
    const addHandle = input({ placeholder: "name to add" });
    const list = h(
      "div",
      { class: "stack" },
      ...conv.members.map((m) =>
        h(
          "div",
          { class: "member-row" },
          h("div", { class: "avatar dm small" }, m.handle.slice(0, 1).toUpperCase()),
          h("span", { class: "mname" }, m.handle, m.user_id === me ? " (you)" : "", m.role === "admin" ? h("span", { class: "tag" }, "admin") : null),
          amAdmin && m.user_id !== me
            ? button(icon("x", 16), async () => {
                try {
                  await this.engine!.removeMember(conv.conv_id, m.user_id);
                  this.toast(`Removed ${m.handle}. Keys rotated.`);
                } catch (err) {
                  this.toast(errText(err), "error");
                }
              }, "icon-btn", "Remove")
            : null,
        ),
      ),
    );
    this.modal(
      conv.title ?? "Group",
      h(
        "div",
        { class: "stack" },
        list,
        amAdmin ? field("Add someone", addHandle, "rotates the group keys") : null,
        amAdmin
          ? button("Add", async () => {
              if (!handleOk(addHandle.value.trim())) return this.toast("Enter a valid name.", "error");
              try {
                await this.engine!.addMember(conv.conv_id, addHandle.value.trim());
                addHandle.value = "";
                this.toast("Added. Keys rotated.");
              } catch (err) {
                this.toast(errText(err), "error");
              }
            }, "btn")
          : null,
      ),
    );
  }

  private newChatDialog(): void {
    const dmHandle = input({ placeholder: "their name" });
    const groupName = input({ placeholder: "group name" });
    const groupMembers = input({ placeholder: "names, comma separated" });
    const close = this.modal(
      "New conversation",
      h(
        "div",
        { class: "stack" },
        h("h3", null, "Direct message"),
        field("Who do you want to message?", dmHandle, "by their Hush name"),
        button("Start chat", async () => {
          if (!handleOk(dmHandle.value.trim())) return this.toast("Enter a valid name.", "error");
          try {
            const id = await this.engine!.startDm(dmHandle.value.trim());
            close();
            this.openConv(id);
          } catch (err) {
            this.toast(errText(err), "error");
          }
        }, "btn primary"),
        h("hr"),
        h("h3", null, "New group"),
        field("Group name", groupName),
        field("Members", groupMembers, "their names, comma separated"),
        button("Create group", async () => {
          const names = groupMembers.value.split(",").map((s) => s.trim()).filter(Boolean);
          if (!groupName.value.trim() || names.length === 0) return this.toast("Add a name and at least one member.", "error");
          try {
            const id = await this.engine!.createGroup(groupName.value.trim(), names);
            close();
            this.openConv(id);
          } catch (err) {
            this.toast(errText(err), "error");
          }
        }, "btn"),
      ),
    );
  }

  private settingsDialog(): void {
    const toggle = (key: keyof Settings, label: string, desc: string, onToggle?: (v: boolean) => void) =>
      h(
        "label",
        { class: "setting" },
        h("div", null, h("div", { class: "s-label" }, label), h("div", { class: "s-desc muted" }, desc)),
        switchEl(this.settings[key], async (v) => {
          this.settings = { ...this.settings, [key]: v };
          await this.engine!.saveSettings(this.settings);
          onToggle?.(v);
        }),
      );
    this.modal(
      "Settings",
      h(
        "div",
        { class: "stack" },
        h("h3", null, "Sharing (off by default)"),
        h("p", { class: "tiny muted" }, "Each of these reveals a little about how you use Hush. You only ever broadcast what you turn on here."),
        toggle("readReceipts", "Read receipts", "Let the people you're chatting with see when you've read their messages."),
        toggle("presence", "Online status", "Share when you're active in a conversation.", () => {
          // Reconnect so the presence flag takes effect immediately.
          if (this.activeConv) this.openConv(this.activeConv);
        }),
        toggle("typing", "Typing indicators", "Show others when you're typing a message."),
        h("hr"),
        h("h3", null, "On this device, always"),
        h("p", { class: "s-desc muted" }, "Messages blur when you switch away, and copy / right-click are limited on message text. Best-effort deterrents, not guarantees."),
        h("hr"),
        h("h3", null, "Account"),
        button("Save recovery file", () => this.downloadRecovery(), "btn"),
        button("Sign out of this device", async () => {
          if (!confirm("Sign out? Locally cached messages on this device will be removed.")) return;
          await logout();
          location.reload();
        }, "btn danger"),
      ),
    );
  }

  private onChange(convId: string): void {
    this.refreshList();
    if (convId === this.activeConv) this.refreshChat();
  }
}

// -------------------------------------------------------------------- helpers

function brandPanel(): HTMLElement {
  return h(
    "div",
    { class: "brand-panel" },
    h("div", { class: "brand-mark" }, icon("lock", 26), "Hush"),
    h("h2", { class: "brand-tag" }, "Say what you mean.", h("br"), "Privately."),
    h("p", { class: "brand-copy" }, "Every message is end-to-end encrypted on your device. We route scrambled text we can't read, and never hold the keys to it."),
    h(
      "ul",
      { class: "brand-points" },
      h("li", null, "No phone number. Just a name and a password."),
      h("li", null, "Disappearing messages and delete-for-everyone."),
      h("li", null, "Open about exactly what we can and can't protect."),
    ),
  );
}

function field(label: string, control: HTMLElement, hint?: string | null): HTMLElement {
  return h("label", { class: "field" }, h("span", { class: "field-label" }, label), control, hint ? h("span", { class: "field-hint" }, hint) : null);
}
function input(attrs: Record<string, unknown>): HTMLInputElement {
  return h("input", { class: "input", ...attrs }) as HTMLInputElement;
}
function button(label: string | HTMLElement, onClick: (e: Event) => void, cls = "btn", title?: string): HTMLElement {
  return h("button", { class: cls, onclick: onClick, ...(title ? { title, "aria-label": title } : {}) }, label);
}
function link(text: string, onClick: () => void): HTMLElement {
  return h("button", { class: "link", onclick: onClick }, text);
}
function menuItem(label: string, ic: HTMLElement, onClick: () => void, cls = ""): HTMLElement {
  return h("button", { class: `menu-item ${cls}`, onclick: onClick }, ic, h("span", null, label));
}
function switchEl(on: boolean, onChange: (v: boolean) => void): HTMLElement {
  const el = h("button", { class: `switch ${on ? "on" : ""}`, role: "switch", "aria-checked": String(on) });
  el.addEventListener("click", () => {
    on = !on;
    el.classList.toggle("on", on);
    el.setAttribute("aria-checked", String(on));
    onChange(on);
  });
  el.append(h("span", { class: "knob" }));
  return el;
}
function convTitle(c: StoredConversation, me: string): string {
  return c.type === "group" ? c.title ?? "Group" : c.members.find((m) => m.user_id !== me)?.handle ?? "Direct message";
}
function shortTime(ts: number): string {
  const d = new Date(ts);
  return d.toDateString() === new Date().toDateString() ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" });
}
const clockTime = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
function fullTime(ts: number): string {
  const d = new Date(ts);
  const today = d.toDateString() === new Date().toDateString();
  return today ? `Today at ${clockTime(ts)}` : `${d.toLocaleDateString([], { month: "short", day: "numeric" })} at ${clockTime(ts)}`;
}
function ttlLabel(sec: number): string {
  if (sec >= 86400) return `${sec / 86400}d`;
  if (sec >= 3600) return `${sec / 3600}h`;
  if (sec >= 60) return `${sec / 60}m`;
  return `${sec}s`;
}
function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function errText(err: unknown): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return "Something went wrong.";
}
